import { Box, Modal, Button, Autocomplete, TextField, TextFieldProps, Typography, useTheme, useMediaQuery, Stack, FilterOptionsState } from '@mui/material';
import { Building02 } from '../../../../../assets';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../../../shared/useUser';
import { Store, Franchise } from '../../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';

const textClamp = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
};

interface SelectStoreModalProps {
  open: boolean,
  onConfirm: () => void,
  onClose: () => void,
}

export const SelectStoreModal = ({ onConfirm, onClose, open }: SelectStoreModalProps) => {
  const { user, setSelectedStore, setSelectedFranchise, selectedFranchise, franchises } = useContext(UserContext);
  const [newStore, setNewStore] = useState<Store | null>(null);
  const [newFranchise, setNewFranchise] = useState<Franchise | null>(null);

  const [showFranchiseDropdown, setShowFranchiseDropdown] = useState<boolean>(false);
  const [showStoreDropdown, setShowStoreDropdown] = useState<boolean>(false);

  const theme = useTheme();
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    if (open) {
      setNewStore(null);
      setNewFranchise(null);
      initializeDropdowns();
    }
  }, [open]);

  const initializeDropdowns = () => {
    if (franchises.length === 1) {
      const singleFranchise = franchises[0];
      setNewFranchise(singleFranchise);
      setShowFranchiseDropdown(false);

      const franchiseStores = (user?.allAccessibleStores ?? user?.stores ?? []).filter(
        s => s.franchiseId === singleFranchise.franchiseId,
      );

      if (franchiseStores.length === 1) {
        const singleStore = franchiseStores[0];
        setNewStore(singleStore);
        setShowStoreDropdown(false);
      } else {
        setShowStoreDropdown(true);
      }
    } else {
      setShowFranchiseDropdown(true);
    }
  };

  const filter = (options: Store[], state: FilterOptionsState<Store>) => {
    return options.filter((option) => option.storeNumber?.startsWith(state.inputValue) || option.storeName?.toLowerCase().includes(state.inputValue.toLowerCase()));
  };

  const updateStore = (store: Store, franchise: Franchise) => {
    setSelectedStore({ ...store, franchiseName: franchise.franchiseName });
    setSelectedFranchise(franchise);
    onConfirm();
    window.location.reload();
  };

  useEffect(() => {
    if (newFranchise) {

      const franchiseStores = (user?.allAccessibleStores ?? user?.stores ?? []).filter(
        s => s.franchiseId === newFranchise.franchiseId,
      );

      setShowStoreDropdown(franchiseStores.length > 0);

      if (franchiseStores.length === 1 && !newStore) {
        setNewStore(franchiseStores[0]);
      }
    }
  }, [newFranchise]);

  const handleChangeFranchise = (franchise: Franchise) => {
    if (franchise) {
      setNewFranchise(franchise);
      setNewStore(null);

      // Use allStores for franchise filtering
      const franchiseStores = (user?.allAccessibleStores ?? user?.stores ?? []).filter(
        s => s.franchiseId === franchise.franchiseId,
      );

      if (franchiseStores.length === 1) {
        setNewStore(franchiseStores[0]);
        setShowStoreDropdown(false);
      } else {
        setShowStoreDropdown(true);
      }
    }
  };
  return (<Modal
    open={open}
    onClose={onClose}
  >
    <Stack
      position='absolute'
      gap={9}
      top='50%'
      left='50%'
      minHeight='200px'
      bgcolor='background.paper'
      border='1px solid grey'
      boxShadow='24px'
      p={6}
      width={`${isMobile ? '300px' : '600px'}`}
      sx={{
        transform: 'translate(-50%, -50%)',
      }}>
      <Box
        display='flex'
        flexDirection='row'
        alignItems='center'
        gap={6}>
        <Building02 />
        <Typography id="modal-modal-title">
          {t('selectAStore')}
        </Typography>
      </Box>
      {showFranchiseDropdown && (
        <Autocomplete
          onChange={(event, newValue: Franchise | null) => {
            if (newValue) {
              handleChangeFranchise(newValue);
            }
          }}
          id="select-franchise"
          options={franchises ?? []}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Franchise) => option.franchiseName ?? ''}
          filterOptions={(options: Franchise[], state) =>
            options.filter(option =>
              option.franchiseName?.toLowerCase().includes(state.inputValue.toLowerCase()),
            )
          }
          renderOption={(props, option) => (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={option.franchiseId}
            >
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant="textSM"
                sx={textClamp}
              >
                {option.franchiseName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              placeholder={t('selectFranchise')}
              {...params as TextFieldProps}
            />
          )}
          value={newFranchise}
        />
      )}

      {showStoreDropdown && (
        <Autocomplete
          onChange={(event, newValue: Store | null) => {
            if (newValue) setNewStore(newValue);
          }}
          id="combo-box-demo"
          options={(user?.allAccessibleStores ?? user?.stores ?? []).filter(s => s.franchiseId == newFranchise?.franchiseId)}
          sx={{ width: '100%', overflow: 'auto', pt: 4 }}
          getOptionLabel={(option: Store) => option.storeName ?? ''}
          filterOptions={(options, state) => filter(options, state)}
          renderOption={(props, option) => (
            <Box component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 }, gap: 2 }}
              {...props}
              key={(option?.storeName ?? '') + (option?.storeNumber ?? '')}>
              <Building02 style={{ minWidth: '24px' }} />
              <Typography
                variant='textSM'
                sx={{
                  ...textClamp,
                }}>
                {option.storeName}
              </Typography>
            </Box>
          )}
          renderInput={(params) => {
            return <TextField placeholder={t('selectAStore')}
              {...params as TextFieldProps} />;
          }}
          value={newStore} />
      )}
      <Box display='flex'
        flexDirection='row'
        justifyContent='center'
        gap={9}>
        <Button variant='secondary'
          onClick={onClose}
          type='button'>{t('dismiss')}</Button>
        <Button
          disabled={!newStore || !newFranchise}
          onClick={() => {
            if (newStore && newFranchise) {
              updateStore(newStore, newFranchise);
            }
          }}
          type='button'
        >
          {t('confirm')}
        </Button>
      </Box>
    </Stack>
  </Modal>);
};
